import type { ValidLocale } from '@localisation'
import type { IWorkflowEventTriggerTypeOption, IWorkflowFilterTypeOption } from '@query'
import type { TFunction } from 'i18next'
import type { Node } from '@xyflow/react'
import type React from 'react'
import type { DefaultTheme } from 'styled-components'

export const WORKFLOW_NODE_TYPES = [
  'scheduled_trigger',
  'event_trigger',
  'filter',
  'selector',
  'wait',
  'luca',
  'audit_trail_comment',
  'email_send',
  'create_document',
  'unknown'
] as const

export type WorkflowNodeType = (typeof WORKFLOW_NODE_TYPES)[number]

export const NODE_CATEGORIES = ['trigger', 'logic', 'ai', 'action'] as const
export type NodeCategory = (typeof NODE_CATEGORIES)[number]

export interface RelativeDatetimeValue {
  kind: 'relative'
  amount: number
  unit: string
}

export interface FilterCondition {
  field: string
  operator: string
  value: string | boolean | number | number[] | null | RelativeDatetimeValue
}

export interface FilterNodeConfig {
  filter_type: string
  match: 'all' | 'any'
  conditions: FilterCondition[]
}

export interface WaitNodeConfig {
  amount: number
  unit: string
}

export interface AuditTrailCommentNodeConfig {
  message: string
}

export interface EmailSendNodeConfig {
  recipients: string[]
  recipient_tags: string[]
  subject: string
  message_source: 'upstream' | 'custom'
  message: string
  language: ValidLocale
}

export type CreateDocumentDateRule =
  | 'monthEnd'
  | 'monthStart'
  | 'prevMonthEnd'
  | 'nextMonthStart'
  | 'plusDays'
  | 'plusMonthsLastDay'
  | 'plusMonthsFirstDay'
  | 'run'

type CreateDocumentDateRuleWithParam = 'plusDays' | 'plusMonthsLastDay' | 'plusMonthsFirstDay'

export type CreateDocumentDateConfig =
  | { source: 'incoming' }
  | { source: 'calc'; rule: Exclude<CreateDocumentDateRule, CreateDocumentDateRuleWithParam> }
  | { source: 'calc'; rule: 'plusDays'; days: number }
  | {
      source: 'calc'
      rule: 'plusMonthsLastDay' | 'plusMonthsFirstDay'
      months: number
    }

export interface CreateDocumentNodeConfig {
  accounting_rule_id: number | null
  date?: CreateDocumentDateConfig
  amount?: number | null
  as_draft?: boolean
  copy_attachments?: boolean
}

export interface LucaNodeConfig {
  instructions: string
  approved_tools: string[]
}

export interface ScheduledTriggerNodeConfig {
  frequency: string
  hour: number
  minute: number
  day_of_week?: number | null
  day_of_month?: number | null
}

export interface EventTriggerNodeConfig {
  event_type: string
}

export type WorkflowNodeConfigByType = {
  event_trigger: EventTriggerNodeConfig
  scheduled_trigger: ScheduledTriggerNodeConfig
  filter: FilterNodeConfig
  selector: FilterNodeConfig
  wait: WaitNodeConfig
  audit_trail_comment: AuditTrailCommentNodeConfig
  email_send: EmailSendNodeConfig
  create_document: CreateDocumentNodeConfig
  luca: LucaNodeConfig
  unknown: Record<string, unknown>
}

export type WorkflowNodeConfigForType<T extends WorkflowNodeType> = WorkflowNodeConfigByType[T]

export type WorkflowNodeConfig = WorkflowNodeConfigByType[WorkflowNodeType]

export interface WorkflowNodeData extends Record<string, unknown> {
  config: Record<string, unknown>
  originalType?: string
}

export type WorkflowFlowNode = Node<WorkflowNodeData, WorkflowNodeType>

export interface SummarizeNodeConfigCatalogs {
  eventTriggerTypes: IWorkflowEventTriggerTypeOption[]
  filterCatalog: IWorkflowFilterTypeOption[]
  lucaToolTitles?: Record<string, string>
  usershipTags?: { id: string; plural: string }[]
  /** Ledger account id → "number name", for filter sentences. */
  accountLabels?: Record<number, string>
  /** Contact id → name, for filter sentences. */
  contactLabels?: Record<number, string>
}

export interface SummarizeNodeConfigOptions {
  /** Render the summary in full instead of trimming it to fit a node card. */
  expanded?: boolean
  /** Label of the immediate upstream step (e.g. for email "message from …"). */
  upstreamLabel?: string
}

export interface HandleSpec {
  id: string
  type: 'source' | 'target'
  labelKey?: string
}

export type NodeConfigFormProps<T extends WorkflowNodeType> = {
  nodeId: string
  config: WorkflowNodeConfigByType[T]
  onChange: (config: WorkflowNodeConfigByType[T]) => void
}

export type NodeConfigFormComponent<T extends WorkflowNodeType = WorkflowNodeType> = React.FC<
  NodeConfigFormProps<T>
>

export interface NodeTypeDefinition<T extends WorkflowNodeType = WorkflowNodeType> {
  type: T
  labelKey: string
  descriptionKey: string
  icon: React.ComponentType
  accentColor: (theme: DefaultTheme) => string
  handles: HandleSpec[]
  defaultConfig: WorkflowNodeConfigByType[T]
  /** Overrides applied on top of `defaultConfig` when the node is added to the canvas. */
  initialConfig?: (context: { language: ValidLocale }) => Partial<WorkflowNodeConfigByType[T]>
  /**
   * Clone config for a copied node. Default is `structuredClone`.
   * Use this to reset instance identity (FKs unique per node, etc.).
   */
  cloneConfig?: (config: WorkflowNodeConfigByType[T]) => WorkflowNodeConfigByType[T]
  paletteOrder: number
  category: NodeCategory
  singleton?: boolean
  /** When set, the source may have at most this many outgoing edges. */
  maxOutgoing?: number
  /** Clamp card summary to a few lines. Defaults to true. */
  clampSummary?: boolean
  /** This step writes text its immediate successor can use. */
  providesTextOutput?: boolean
  paletteHidden?: boolean
  inspectorWidth?: number
  inspectorFlush?: boolean
  summarize: (
    config: Record<string, unknown>,
    t: TFunction,
    catalogs: SummarizeNodeConfigCatalogs,
    options: SummarizeNodeConfigOptions
  ) => React.ReactNode
  configForm?: NodeConfigFormComponent<T>
}

export const isWorkflowNodeType = (type: string | undefined | null): type is WorkflowNodeType =>
  WORKFLOW_NODE_TYPES.includes(type as WorkflowNodeType)
